import { pickVariant, AB_CLOSING_COPY, readAbVariantId } from '@/lib/assistantABTesting';
import { trackAssistantEvent } from '@/lib/assistantAnalytics';

/** Mensajes del chat y helpers de cantidad en cards. */
export function bindAssistantChatMessages(ctx) {
  const {
    messagesEndRef,
    silentNudgeTimeoutRef,
    setMessages,
    assistantCardQty,
    setAssistantCardQty,
    benefitsProgressSnapshot,
    assistantAnalyticsCtx,
    freeTextInputRef,
  } = ctx;

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getAssistantLineQty = (key) => assistantCardQty[key] ?? 1;

  const bumpAssistantLineQty = (key, delta) => {
    setAssistantCardQty((prev) => {
      const c = prev[key] ?? 1;
      return { ...prev, [key]: Math.max(1, Math.min(99, c + delta)) };
    });
  };

  const addBotMessage = (text, options = null, products = null, meta = {}) => {
    const ts = new Date();
    const id = `bot-${ts.getTime()}-${Math.random().toString(36).slice(2, 9)}`;
    let index = 0;
    setMessages((prev) => {
      index = prev.length;
      return [
        ...prev,
        {
          id,
          type: 'bot',
          text,
          options,
          products,
          preProductOptions: meta.preProductOptions?.length ? meta.preProductOptions : undefined,
          postAddAddonCards: meta.postAddAddonCards?.length ? meta.postAddAddonCards : undefined,
          textAfterProducts: meta.textAfterProducts || undefined,
          suggestedProducts: meta.suggestedProducts?.length ? meta.suggestedProducts : undefined,
          productCardMode: meta.productCardMode,
          productsScrollViewport: meta.productsScrollViewport === true,
          productsScrollVisibleCount:
            typeof meta.productsScrollVisibleCount === 'number'
              ? meta.productsScrollVisibleCount
              : undefined,
          orderSummary: meta.orderSummary || undefined,
          orderSummaryEditable: meta.orderSummaryEditable === true,
          skipUpsellForProductAdds: meta.skipUpsellForProductAdds === true,
          silentNudgeSent: false,
          timestamp: ts,
        },
      ];
    });
    return { index, id };
  };

  const patchMessageAt = (index, patch) => {
    if (index == null || index < 0) return;
    setMessages((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const patchMessageById = (id, patch) => {
    if (!id) return;
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  /** Actualiza la tarjeta de resumen más reciente (evita índices desfasados). */
  const patchLastOrderSummary = (preview) => {
    if (!preview) return;
    setMessages((prev) => {
      let lastIdx = -1;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i]?.orderSummary) {
          lastIdx = i;
          break;
        }
      }
      if (lastIdx < 0) return prev;
      return prev.map((m, i) => (i === lastIdx ? { ...m, orderSummary: preview } : m));
    });
  };

  const refreshOrderSummaries = (preview) => {
    setMessages((prev) =>
      prev.map((m) => (m.orderSummary ? { ...m, orderSummary: preview } : m))
    );
  };

  const clubNudgeBeforeCloseLine = () => {
    const b = benefitsProgressSnapshot;
    if (!b?.enabled) return '';
    if (b.available_rewards > 0) return '';
    if (b.remaining_orders !== 1) return '';
    return 'Te falta 1 compra para tu beneficio 👀';
  };

  const buildAfterUpsellClosingText = () => {
    const nudge = clubNudgeBeforeCloseLine();
    const v = pickVariant('closing_copy', AB_CLOSING_COPY);
    const close = v.id === 'A' ? `${v.text} 👇` : v.text;
    return nudge ? `${nudge}\n\n${close}` : close;
  };

  const trackClosingPromptShown = () => {
    const vid = readAbVariantId('closing_copy');
    if (!vid) return;
    trackAssistantEvent('assistant_closing_prompt_shown', {
      ...assistantAnalyticsCtx(null),
      detected_intent: 'closing_prompt',
      ab_test_key: 'closing_copy',
      ab_variant_id: vid,
    });
  };

  const addUserMessage = (text) => {
    if (silentNudgeTimeoutRef.current) {
      clearTimeout(silentNudgeTimeoutRef.current);
      silentNudgeTimeoutRef.current = null;
    }
    setMessages((prev) => [
      ...prev,
      {
        type: 'user',
        text,
        timestamp: new Date(),
      },
    ]);
  };

  const focusAssistantChatInput = () => {
    setTimeout(() => {
      try {
        freeTextInputRef.current?.focus?.();
      } catch {
        // ignore
      }
    }, 200);
  };

  return {
    scrollToBottom,
    getAssistantLineQty,
    bumpAssistantLineQty,
    addBotMessage,
    patchMessageAt,
    patchMessageById,
    patchLastOrderSummary,
    refreshOrderSummaries,
    addUserMessage,
    clubNudgeBeforeCloseLine,
    buildAfterUpsellClosingText,
    trackClosingPromptShown,
    focusAssistantChatInput,
  };
}
